import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import axios from 'axios';
import LoadingScreen from '../component/LoadingScreen';
import FormRenderer from '../component/FormRenderer';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { CallerInfo, TemplateFormData, TemplateSchema, SUPPORTED_LANGUAGES } from '../types/template';

// ── Identity card shown in the form header ────────────────────────────────────
function CallerCard({ caller, accessMode, photoUrl, msalName, msalEmail }: {
    caller:     CallerInfo | null;
    accessMode: string | undefined;
    photoUrl:   string | null;
    msalName:   string | null;
    msalEmail:  string | null;
}) {
    if (accessMode === 'Anonymous') {
        return (
            <div className="form-caller-card form-caller-card--anonymous">
                <div className="form-caller-card__avatar form-caller-card__avatar--anon">?</div>
                <span className="form-caller-card__label">Responding anonymously</span>
            </div>
        );
    }

    if (accessMode === 'Organization') {
        // Prefer Employee-table data; fall back to MSAL token claims for Azure AD accounts not in local DB
        const displayName = caller?.name ?? msalName ?? msalEmail ?? '-';
        const displaySub  = caller?.employeeId
            ? `ID: ${caller.employeeId}`
            : (msalEmail ?? null);
        const initial = displayName !== '-'
            ? displayName.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
            : '?';

        return (
            <div className="form-caller-card">
                {photoUrl
                    ? <img src={photoUrl} className="form-caller-card__avatar form-caller-card__avatar--photo" alt="avatar" />
                    : <div className="form-caller-card__avatar form-caller-card__avatar--initials">{initial}</div>
                }
                <div className="form-caller-card__info">
                    <div className="form-caller-card__name">{displayName}</div>
                    {displaySub && <div className="form-caller-card__sub">{displaySub}</div>}
                </div>
            </div>
        );
    }

    if (accessMode === 'InvitedOnly' && caller) {
        return (
            <div className="form-caller-card">
                <div className="form-caller-card__avatar form-caller-card__avatar--initials">
                    {(caller.name ?? caller.email ?? '?')[0].toUpperCase()}
                </div>
                <div className="form-caller-card__info">
                    {caller.name && <div className="form-caller-card__name">{caller.name}</div>}
                    {caller.email && <div className="form-caller-card__sub">{caller.email}</div>}
                </div>
            </div>
        );
    }

    return null;
}

export default function FormFill() {
    const { code }                  = useParams<{ code: string }>();
    const { instance, accounts, inProgress } = useMsal();
    const navigate                  = useNavigate();

    const [loading, setLoading]     = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [token, setToken]         = useState('');
    const [photoUrl, setPhotoUrl]   = useState<string | null>(null);

    const [formData, setFormData]   = useState<TemplateFormData | null>(null);
    const [schema, setSchema]       = useState<TemplateSchema>({ fields: [] });
    const [currentLang, setCurrentLang] = useState<string>('en');
    const [initialAnswers, setInitialAnswers] = useState<Record<string, unknown>>({});
    const [lockedFields, setLockedFields]   = useState<Record<string, unknown>>({});
    const [submitted, setSubmitted]       = useState(false);
    const [justSubmitted, setJustSubmitted] = useState(false);

    const api = process.env.REACT_APP_API_END_POINT;

    // ── Auth init ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        acquireGetAccessToken(instance, accounts).then(async (tokens: any) => {
            if (!tokens) return;
            setToken(tokens.accessToken);
            // Fetch MS Graph profile photo (used for Organization identity card)
            if (tokens.accessTokenProfile) {
                axios.get('https://graph.microsoft.com/v1.0/me/photo/$value', {
                    headers: { Authorization: `Bearer ${tokens.accessTokenProfile}` },
                    responseType: 'arraybuffer',
                }).then(r => {
                    const blob = new Blob([r.data], { type: 'image/jpeg' });
                    setPhotoUrl(URL.createObjectURL(blob));
                }).catch(() => { /* no photo */ });
            }
        });
    }, [inProgress, instance, accounts]); // eslint-disable-line

    // ── Load form ─────────────────────────────────────────────────────────────
    // Supports optional auth: Anonymous forms work without a token.
    // If not logged in and the form returns 401, redirect to MSAL login.
    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        if (!code) return;
        // If accounts are present but token hasn't been acquired yet, wait.
        if (accounts.length > 0 && !token) return;

        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get<TemplateFormData>(
            `${api}/v1/Form/form`,
            { headers, params: { code } }
        )
            .then(res => {
                setFormData(res.data);
                let parsedSchema: TemplateSchema = { fields: [] };
                if (res.data.template.schemaJson) {
                    try {
                        parsedSchema = JSON.parse(res.data.template.schemaJson);
                        setSchema(parsedSchema);
                        if (parsedSchema.languages?.length) setCurrentLang(parsedSchema.languages[0]);
                    } catch { /* invalid */ }
                }
                if (res.data.myAnswersJson) {
                    try {
                        setInitialAnswers(JSON.parse(res.data.myAnswersJson));
                    } catch { /* invalid */ }
                }
                // Auto-fill and lock known caller fields (e.g. Employee ID)
                const empId = res.data.caller?.employeeId;
                if (empId) {
                    const empField = parsedSchema.fields.find(f =>
                        /employee[\s_-]?id|รหัสพนักงาน|emp[\s_-]?id/i.test(f.label)
                    );
                    if (empField) {
                        setLockedFields({ [empField.id]: empId });
                    }
                }
                if (res.data.hasSubmitted && !res.data.canEdit) {
                    setSubmitted(true);
                }
            })
            .catch(err => {
                const status = err?.response?.status;
                if (status === 401) { instance.loginRedirect(); return; }
                if (status === 403) { navigate('/accessdenied'); return; }
                toast.error(err?.response?.data?.message ?? 'Failed to load form');
            })
            .finally(() => setLoading(false));
    }, [token, code, inProgress, accounts]); // eslint-disable-line

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async (answers: Record<string, unknown>) => {
        if (!formData?.canEdit) return;
        setSubmitting(true);
        const submitHeaders: Record<string, string> = { 'Content-type': 'application/json' };
        if (token) submitHeaders['Authorization'] = `Bearer ${token}`;

        try {
            await axios.post(
                `${api}/v1/Form/submit`,
                {
                    templateCode: code,
                    answersJson: JSON.stringify(answers),
                },
                { headers: submitHeaders }
            );
            toast.success('Response submitted successfully!');
            setSubmitted(true);
            setJustSubmitted(true);
            setFormData(prev => prev ? { ...prev, hasSubmitted: true, canEdit: prev.template.allowEditResponse } : prev);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? 'Submit failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <LoadingScreen />;

    if (!formData) {
        return (
            <div className="template-fill-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="text-center text-muted">
                    <div style={{ fontSize: 48 }}>📋</div>
                    <p>Form not found or not available.</p>
                </div>
            </div>
        );
    }

    const { template } = formData;

    return (
        <div className="template-fill-page">
            <ToastContainer position="top-right" autoClose={3000} />
                <div className="template-fill-page__card">
                    {/* Header */}
                    <div className="template-fill-page__header">
                        <img src="/logo-pandora.png" alt="Pandora" className="template-fill-page__header-logo" />
                        {/* Language toggle — shown only when form has multiple languages */}
                        {(schema.languages?.length ?? 0) > 1 && (
                            <div className="form-lang-toggle">
                                {schema.languages!.map(l => {
                                    const ln = SUPPORTED_LANGUAGES.find(x => x.code === l)?.name ?? l.toUpperCase();
                                    return (
                                        <button
                                            key={l}
                                            type="button"
                                            className={`form-lang-toggle__btn${currentLang === l ? ' form-lang-toggle__btn--active' : ''}`}
                                            onClick={() => setCurrentLang(l)}
                                        >
                                            {ln}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <h2>{schema.names?.[currentLang] || schema.names?.['en'] || template.name}</h2>
                        {(schema.descriptions?.[currentLang] || schema.descriptions?.['en'] || template.description) && (
                            <p>{schema.descriptions?.[currentLang] || schema.descriptions?.['en'] || template.description}</p>
                        )}
                        {template.closeDate && (
                            <p style={{ opacity: .7, fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                                Closes: {moment.utc(template.closeDate).tz('Asia/Bangkok').format('DD MMM YYYY HH:mm')}
                            </p>
                        )}
                        <CallerCard
                            caller={formData.caller}
                            accessMode={template.accessMode}
                            photoUrl={photoUrl}
                            msalName={accounts[0]?.name ?? null}
                            msalEmail={accounts[0]?.username ?? null}
                        />
                    </div>

                    {/* Body */}
                    <div className="template-fill-page__body">
                        {/* Success state — shown immediately after submission */}
                        {justSubmitted ? (
                            <div className="text-center py-5">
                                <div style={{ fontSize: 56 }}>✅</div>
                                <h4 className="mt-3 fw-bold">Response Submitted!</h4>
                                <p className="text-muted mb-4">Thank you for completing this form.</p>
                                {template.allowEditResponse && (
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => setJustSubmitted(false)}
                                    >
                                        Edit My Answers
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Not open window */}
                                {!formData.isOpen && (
                                    <div className="template-fill-page__info-banner">
                                        {template.openDate && new Date() < new Date(template.openDate)
                                            ? `This form opens on ${moment.utc(template.openDate).tz('Asia/Bangkok').format('DD MMM YYYY HH:mm')}.`
                                            : 'This form is closed and no longer accepting responses.'
                                        }
                                    </div>
                                )}

                                {/* Already submitted (no edit allowed) — only for returning visitors */}
                                {submitted && !formData.canEdit && !justSubmitted && (
                                    <div className="template-fill-page__submitted-banner">
                                        ✓ You have already submitted a response for this form.
                                        {' Editing is not allowed for this form.'}
                                    </div>
                                )}

                                {/* Form */}
                                {schema.fields.length > 0 ? (
                                    <FormRenderer
                                        schema={schema}
                                        initialAnswers={initialAnswers}
                                        lockedFields={lockedFields}
                                        readOnly={!formData.canEdit || !formData.isOpen}
                                        onSubmit={formData.canEdit && formData.isOpen ? handleSubmit : undefined}
                                        submitting={submitting}
                                        submitLabel={formData.hasSubmitted && !justSubmitted ? 'Update Response' : 'Submit'}
                                        currentLang={currentLang}
                                    />
                                ) : (
                                    <div className="text-center text-muted py-5">This form has no questions yet.</div>
                                )}
                            </>
                        )}
                    </div>

                {/* ── Footer — inside the card ───────────────────── */}
                <footer className="template-fill-page__footer">
                    <div className="template-fill-page__footer-inner">
                        <div className="template-fill-page__footer-brand">
                            <img src="/logo-pandora.png" alt="Pandora" className="template-fill-page__footer-logo" />
                            <span className="template-fill-page__footer-sub">HR Event Management System</span>
                        </div>
                        <div className="template-fill-page__footer-meta">
                            <span>🔒 Internal Use Only</span>
                            <span className="template-fill-page__footer-dot">·</span>
                            <span>© {new Date().getFullYear()} Pandora</span>
                        </div>
                    </div>
                </footer>
                </div>

            </div>
    );
}
